import { html, LitElement, PropertyValues, TemplateResult, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import stylesSleepingAnimation from '../../../../styles/animations/SnoozeAnimation.scss?inline';
import { ESexo, EStatusSleepChild, IAlumno } from "../../../data/models/Entities";
import { _SvAlumnoFotoPerfilURL } from "../../../data/services/Alumno";
import { EPhotoSize } from "../../../data/services/Photo";
import "../Avatar/Avatar";
import { AvatarComponent } from "../Avatar/Avatar";
import styles from './AvatarAlumno.scss?inline';

type IAvatarAlumnoBasic = Required<Pick<IAlumno, "IdUsuario" | "Modificacion" | "Sexo" | "Nombre">>
    & Partial<Pick<IAlumno, "EstadoEvt" | "_NombreCompleto" | "ApPaterno" | "ApMaterno">>;

export type IAvatarAlumnoNameMode = "short" | "full";

const DEFAULT_BORDER_WIDTH_PX = 3;
const BORDER_MIN_WIDTH = 1;
const BORDER_MAX_WIDTH = 10;
const BORDER_WIDTH_RATIO = 0.04; // 4%

/**
 * Componente web para mostrar el avatar de un alumno con un borde de color que indica su sexo,
 * y opcionalmente un indicador de estado de sueño.
 *
 * Características:
 * * El tamaño del borde se calcula como el {@link BORDER_WIDTH_RATIO} de la diagonal
 *      del rectángulo definido por las propiedades _width y _height,
 *      limitado por {@link BORDER_MIN_WIDTH} y {@link BORDER_MAX_WIDTH}.
 * * El componente utiliza el componente <app-avatar> para mostrar la imagen del alumno,
 *      y muestra su nombre como texto alternativo.
 * * El color del borde se determina por el sexo del alumno (masculino, femenino o desconocido),
 *     pero puede ser sobrescrito por la propiedad k_colorBorder.
 *
 * Propiedades importantes:
 * @property {IAlumno} k_alumno - Objeto con la información del alumno. Se requiere al menos IdUsuario, Modificacion, Sexo y Nombre.
 * @property {string} k_width - Ancho del avatar (incluyendo el borde). Puede ser un valor CSS como "60px" o "var(--avatar-size)". El borde se ajusta automáticamente.
 * @property {string} k_height - Alto del avatar (incluyendo el borde). Puede ser un valor CSS como "60px" o "var(--avatar-size)". El borde se ajusta automáticamente.
 * @property {"short" | "full"} k_altNameMode - Modo de visualización del nombre del alumno en el texto alternativo (alt) de la imagen. "short" muestra iniciales o primeras palabras, "full" muestra el nombre completo.
 *
 * @example
 * <app-avatar-alumno
 *   .k_alumno=${alumno}
 *   .k_width="80px"
 *   .k_height="80px"
 * ></app-avatar-alumno>
 *
 * Nota: El componente asume que la función _SvAlumnoFotoPerfilURL y el enum EPhotoSize están disponibles globalmente para obtener la URL de la foto del alumno.
 */
@customElement('app-avatar-alumno')
export class AvatarAlumnoComponent extends LitElement {
    @property({ type: Object }) k_alumno: IAvatarAlumnoBasic | null = null;
    /**
     * Si se especifica, se muestra el recurso en lugar de la foto de perfil del alumno. Puede ser:
     * * URL de la imagen del avatar
     * * Base64 data URL (no usa cache)
     * */
    @property({ type: String, reflect: true }) public k_src: string | null = null;
    @property({ type: String, reflect: true }) k_width: string = "60px";
    @property({ type: String, reflect: true }) k_height: string = "60px";
    @property({ type: Number, reflect: true }) k_minBorderWidth: number = BORDER_MIN_WIDTH;
    @property({ type: Number, reflect: true }) k_maxBorderWidth: number = BORDER_MAX_WIDTH;
    /** Si se especifica, se usa este color para el borde en lugar del color por defecto basado en el sexo */
    @property({ type: String, reflect: true }) k_colorBorder: string = null;
    /**
     * Define el modo de visualización del nombre del alumno en el texto alternativo (alt) de la imagen.
     * - "short": Muestra las iniciales del nombre del alumno o las dos primeras palabras (por ejemplo, "JP" para "Juan Pérez").
     * - "full": Muestra el nombre o nombre completo del alumno.
     */
    @property({ type: String, reflect: true }) k_altNameMode: IAvatarAlumnoNameMode = "short";
    @property({ type: String, reflect: true }) k_defaultAvatarSVG: string | null = null;

    @query('app-avatar') avatarElement!: AvatarComponent;
    @query('.border') borderElement!: HTMLElement;

    static styles = [unsafeCSS(styles), unsafeCSS(stylesSleepingAnimation)];

    // **********************************************************************
    // Ciclo de vida del componente
    // **********************************************************************

    render(): TemplateResult {
        const borderColor = this.getBorderColor();
        const borderWidth = this.getBorderWidth();
        const nombreAlt = this.getAlumnoNombreAlt();
        const src = this.getAlumnoSrc();
        const useCompleteName = this.k_altNameMode === "full";
        const widthValue = this.getWidthValue();
        const heightValue = this.getHeightValue();

        return html`
			<span
				class="border"
				style="border-width: ${borderWidth}; border-color: ${borderColor}; border-style: solid;"
			>
				<app-avatar
                    .k_useCache=${true}
                    .k_src=${src}
                    .k_width=${`calc(${widthValue}px - (${borderWidth} * 2))`}
                    .k_height=${`calc(${heightValue}px - (${borderWidth} * 2))`}
                    .k_defaultAvatarSVG=${this.k_defaultAvatarSVG}
                    .k_altTextFontSizePxMin=${10}
                    .k_altTextFontSizeScalingFactor=${useCompleteName ? 0.13 : 0.25}
                    .k_alt=${useCompleteName ? null : nombreAlt /** Internamente el componente muestra las iniciales del nombre */}
                >
					${useCompleteName ? html`<span slot="k_alt">${nombreAlt}</span>` : null}
				</app-avatar>

                ${this.renderSleepIndicator()}
                <slot></slot>
			</span>
		`;
    }

    private renderSleepIndicator(): TemplateResult | null {
        if (!this.k_alumno || this.k_alumno.EstadoEvt !== EStatusSleepChild.DORMIDO) return null;
        const fontSize = Math.max(10, Math.min(30, this.getWidthValue() * 0.4));
        return html`<div class="anim-snooze-container">
                <div class="snooze-animation">
                    <span style="font-size: ${fontSize}px;">z</span>
                    <span style="font-size: ${fontSize}px;">z</span>
                    <span style="font-size: ${fontSize}px;">z</span>
                    <span style="font-size: ${fontSize}px;">z</span>
                </div>
            </div>`;
    }

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);

        if (changedProperties.has("k_width")) {
            if (this.k_width) {
                this.style.setProperty('--aa-width', this.k_width);
            } else {
                this.style.removeProperty('--aa-width');
            }
        }
        if (changedProperties.has("k_height")) {
            if (this.k_height) {
                this.style.setProperty('--aa-height', this.k_height);
            } else {
                this.style.removeProperty('--aa-height');
            }
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        if (!this.k_width?.endsWith("px") || !this.k_height?.endsWith("px")) {
            // Forzar actualización del borde después de que el componente haya sido renderizado y tenga dimensiones definidas
            this.requestUpdate();
        }
    }

    // **********************************************************************
    // Utilities
    // **********************************************************************

    private getWidthValue(): number {
        // if (this.avatarElement) {
        //     return this.avatarElement._getWidthValue();
        // }
        if (this.k_width?.endsWith("px")) {
            const w = parseFloat(this.k_width);
            return isNaN(w) ? 40 : w;
        }
        const avatarWidth = this.offsetWidth || 40;
        return avatarWidth;
    }

    private getHeightValue(): number {
        // if (this.avatarElement) {
        //     return this.avatarElement._getHeightValue();
        // }
        if (this.k_height?.endsWith("px")) {
            const h = parseFloat(this.k_height);
            return isNaN(h) ? 40 : h;
        }
        const avatarHeight = this.offsetHeight || 40;
        return avatarHeight;
    }

    private getBorderColor(): string {
        if (this.k_colorBorder) return this.k_colorBorder;
        if (!this.k_alumno) return 'var(--color_borderbox1)';
        if (this.k_alumno.Sexo === ESexo.FEMENINO) return 'var(--color-girl)';
        if (this.k_alumno.Sexo === ESexo.MASCULINO) return 'var(--color-child)';
        return 'var(--color-gender-unknown)';
    }

    /**
     * Calcula el tamaño del borde como el {@link BORDER_WIDTH_RATIO} de la diagonal del rectángulo definido por _width y _height
     * Si no se pueden parsear las dimensiones, devuelve un valor por defecto de 3px.
     * El borde mínimo es de 1px para asegurar que siempre se vea.
    */
    private getBorderWidth(): string {
        const w = this.getWidthValue();
        const h = this.getHeightValue();
        if (isNaN(w) || isNaN(h)) return DEFAULT_BORDER_WIDTH_PX + 'px';
        const diagonal = Math.sqrt(w * w + h * h);
        const minBorder = (this.k_minBorderWidth == null || this.k_minBorderWidth < 0) ? BORDER_MIN_WIDTH : this.k_minBorderWidth;
        const maxBorder = (this.k_maxBorderWidth == null || this.k_maxBorderWidth < 0) ? BORDER_MAX_WIDTH : this.k_maxBorderWidth;
        const calculatedBorder = Math.round(diagonal * BORDER_WIDTH_RATIO);
        const borderWidth = Math.max(minBorder, Math.min(calculatedBorder, maxBorder));
        return borderWidth + 'px';
    }

    private getAlumnoNombreAlt(): string {
        if (!this.k_alumno) return '';
        if (this.k_altNameMode === "short") {
            const nombreCompleto = this.k_alumno._NombreCompleto
                || (this.k_alumno.Nombre + " " + this.k_alumno.ApPaterno + " " + this.k_alumno.ApMaterno).trim() || '';
            return nombreCompleto;
        } else {
            return this.k_alumno.Nombre || this.k_alumno._NombreCompleto || '';
        }
    }

    private getAlumnoSrc(): string | null {
        if (this.k_src) return this.k_src;
        if (!this.k_alumno) return null;
        return _SvAlumnoFotoPerfilURL(this.k_alumno, EPhotoSize.THUMBNAIL);
    }
}